import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { useTurnstileWidget } from '~/hooks/useTurnstileWidget';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { KYCStatus } from '~/api/KYCStatus';
import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import { ModalDokobitStatusCheck } from '~/components/ModalDokobitStatusCheck';
import styles from '~/components/ModalDokobitStatusCheck/ModalDokobitStatusCheck.module.scss';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';

import { mainStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';

import CodeVerificationForm from './components/CodeVerificationForm';
import PhoneNumberForm from './components/PhoneNumberForm';
import { isTestPhoneNumber } from './components/PhoneNumberForm/utils';
import { useModalAuthHeim } from './hooks';

const ModalAuthHeimp = ({ isShow }: { isShow: boolean }) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const navigate = useNavigate();

  const {
    type,
    step,
    personalCode,
    phoneVal,
    phoneValid,
    phoneError,
    policyAgreement,
    policyError,
    codeVal,
    isCodeValid,
    isPendingCode,
    isLoading,
    isNativeAuthLoading,
    canRequestAgain,
    isDeletedUser,
    isDokobitCheckStep,
    dokobitCheckData,
    dokobitError,
    isDokobitFailed,
    timeoutEnabled,
    isDokobitCheckBackConfirming,
    phoneNumber,
    captchaWidgetIdRef,
    setType,
    setStep,
    setPersonalCode,
    setIsDokobitCheckBackConfirming,
    setCanRequestAgain,
    setIsDokobitFailed,
    backHandler,
    handleTimeoutTick,
    handlePhoneValueChange,
    handlePolicyChange,
    requestCodeWithDokobit,
    requestCodeRegular,
    handlePhoneValidate,
    handleCodeChange,
    handleSubmitNumber,
    handleSubmitCode,
    handleConfirmCancel,
    handleTimeoutEnd,
    getCaptchaToken,
    handleDokobitCheckSuccess,
    handleSwitchToSmsVerification,
    handleBackFromCodeForm,
    handleTryAgainWithDokobit,
    handleTrySmsOtp,
    handleDokobitConfirmCancel,
    handleChangeNumber,
    handleDokobitError,
  } = useModalAuthHeim();

  const { isMobile } = useGlobal();

  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const isCaptchaEnabled = company.isEnabledCaptcha && !mainStore.isRN;

  useTurnstileWidget({
    containerRef: captchaContainerRef,
    widgetIdRef: captchaWidgetIdRef,
    enabled: isCaptchaEnabled,
    siteKey: mainStore.captchaSiteID,
    onSuccess: () => requestCodeWithDokobit(),
  });

  useEffect(() => {
    if (!userStore.isAuthorized || !isShow) {
      return;
    }
    if (
      userStore.isAuthorized &&
      userStore.personalData.kycStatus !== KYCStatus.Verified
    ) {
      navigate('/cabinet/personal-data');
    }
    closeModal([REDIRECT_QUERY_NAME]);
  }, [userStore.isAuthorized, isShow]);

  const renderDokobitFailModal = () => {
    if (!isMobile) {
      return null;
    }

    const handleCloseModal = () => {
      setIsDokobitFailed(false);
    };

    const handleContinue = () => {
      handleCloseModal();
      requestCodeRegular();
    };

    const handleTryAgain = () => {
      handleCloseModal();
      handleTryAgainWithDokobit();
    };

    const isTestNumber = isTestPhoneNumber(phoneNumber.current);

    const element = (
      <Modal
        isShow={isDokobitFailed}
        className="dokobit-auth-fail"
        onBackdropDismiss={handleCloseModal}
      >
        <div className="dokobit-auth-fail__body">
          <div className="dokobit-auth-fail__titleHead">
            <h3>{t('phrases:phoneNotFoundInDatabase')}</h3>
            <button
              data-button="back"
              className="button _no-padding _no-color back-button"
              onClick={handleCloseModal}
            >
              <Icon type="close" size={24} />
            </button>
          </div>
          <p>
            {isTestNumber
              ? t('phrases:phoneNotFoundDescription')
              : t('phrases:phoneNotFoundDescriptionNoOtp')}
          </p>
        </div>
        <div className="dokobit-auth-fail__actions">
          <button
            type="button"
            className="button _bordered _med"
            disabled={isNativeAuthLoading}
            onClick={handleTryAgain}
          >
            {t('phrases:tryAgain')}
          </button>
          {isTestNumber ? (
            <button
              type="button"
              className="button _primary _med"
              disabled={isNativeAuthLoading}
              onClick={handleContinue}
            >
              {t('phrases:continue')}
            </button>
          ) : (
            <button
              type="button"
              className="button _primary _med"
              disabled={isNativeAuthLoading}
              onClick={handleChangeNumber}
            >
              {t('changeNumber')}
            </button>
          )}
        </div>
      </Modal>
    );

    if (typeof document === 'undefined') {
      return null;
    }

    const node = document.getElementById('app');

    if (!node) {
      return null;
    }

    return createPortal(element, node);
  };

  const renderConfirmModal = () => {
    if (typeof document === 'undefined') {
      return null;
    }

    const node = document.getElementById('app');

    if (!node) {
      return null;
    }

    const handleCloseConfirmModal = () => {
      setIsDokobitCheckBackConfirming(false);
    };

    return createPortal(
      <Modal
        isShow={isDokobitCheckBackConfirming}
        className={styles.confirmModal}
      >
        <div className={styles.confirmModal__header}>
          <h3>{t('phrases:leaveVerification')}</h3>
          <button
            data-button="back"
            className="button _no-padding _no-color back-button"
            onClick={handleCloseConfirmModal}
          >
            <Icon type={'close'} size={24} />
          </button>
        </div>
        <div className={styles.confirmModal__content}>
          <p>{t('phrases:leaveVerificationBody')}</p>
          <span>{t('phrases:leaveVerificationCaption')}</span>
        </div>
        <div className={styles.confirmModal__actions}>
          <button
            className="button _bordered"
            type="button"
            onClick={handleConfirmCancel}
          >
            {t('cancel')}
          </button>
          <button
            className="button _primary"
            type="button"
            onClick={handleCloseConfirmModal}
          >
            {t('phrases:continueVerification')}
          </button>
        </div>
      </Modal>,
      node,
    );
  };

  const renderBody = () => {
    if (isDokobitCheckStep) {
      return (
        <ModalDokobitStatusCheck
          step={step}
          type={type}
          personalCode={personalCode}
          phoneNumber={phoneNumber.current}
          dokobitCheckData={dokobitCheckData}
          dokobitError={dokobitError}
          captchaToken={getCaptchaToken()}
          requestCodeWithDokobit={requestCodeWithDokobit}
          onSuccess={handleDokobitCheckSuccess}
          onSwitchToSmsVerification={handleSwitchToSmsVerification}
          onVerificationTypeSelect={setType}
          onPersonalCodeChange={setPersonalCode}
          onStepChange={setStep}
          onError={handleDokobitError}
        />
      );
    }

    return !isPendingCode ? (
      <PhoneNumberForm
        phoneVal={phoneVal}
        policyAgreement={policyAgreement}
        policyError={policyError}
        isLoading={isLoading}
        dokobitError={dokobitError}
        dokobitFailed={isDokobitFailed}
        phoneError={phoneError}
        onPhoneChange={handlePhoneValueChange}
        onPhoneValidate={handlePhoneValidate}
        onPolicyChange={handlePolicyChange}
        onSubmit={handleSubmitNumber}
        enableTimeout={timeoutEnabled}
        onTimeoutTick={handleTimeoutTick}
        onTimeoutEnd={handleTimeoutEnd}
        onTryAgainWithDokobit={handleTryAgainWithDokobit}
        onTrySmsOtp={handleTrySmsOtp}
        onChangeNumber={handleChangeNumber}
      />
    ) : (
      <CodeVerificationForm
        phoneNumber={phoneNumber.current}
        codeVal={codeVal}
        isCodeValid={isCodeValid}
        phoneValid={phoneValid}
        isLoading={isLoading}
        isDeletedUser={isDeletedUser}
        canRequestAgain={canRequestAgain}
        requestCode={requestCodeRegular}
        setCanRequestAgain={setCanRequestAgain}
        onCodeChange={handleCodeChange}
        onSubmit={handleSubmitCode}
        onChangePhone={handleBackFromCodeForm}
      />
    );
  };

  return (
    <Modal
      isShow={isShow}
      className="modal-auth__body"
      onBackdropDismiss={
        dokobitCheckData ? handleDokobitConfirmCancel : backHandler
      }
    >
      <div className="auth-modal" data-company={company.name}>
        <button
          data-button="back"
          className="button _no-padding _no-color back-button"
          onClick={dokobitCheckData ? handleDokobitConfirmCancel : backHandler}
        >
          <Icon
            type={isMobile ? 'arrow' : 'close'}
            className={isMobile ? 'icon__rotate-180' : ''}
            size={isMobile ? 24 : 32}
          />
        </button>

        {renderBody()}

        {isCaptchaEnabled && <div ref={captchaContainerRef} />}
      </div>
      {renderDokobitFailModal()}
      {renderConfirmModal()}
    </Modal>
  );
};

export default observer(ModalAuthHeimp);
